/**
 * Owner: Masa Bayu
 * File: component-primitives.ts
 * Date:Time: 2026-09-13 21:46
 * Description: 22F component primitive catalog + token validation.
 */
import { createQXComponentPrimitive, isQXComponentPrimitive, } from './component-primitive';
import { createQXDesignTokens, createQXDesignTokensFromSystem, hasQXDesignToken, validateQXDesignTokens, } from './design-tokens';
import { formatQXDesignTokenPath } from './design-token';
export const QX_COMPONENT_PRIMITIVES_MODEL_VERSION = '1.0.0';
export const QX_COMPONENT_PRIMITIVES_FORBIDDEN_KEYS = [
    'react',
    'jsx',
    'dom',
    'html',
    'css',
    'className',
    'style',
    'render',
    'renderer',
    'children',
    'props',
    'qxw',
    'qxp',
    'widget',
];
function assertNoForbidden(record, label) {
    for (const key of QX_COMPONENT_PRIMITIVES_FORBIDDEN_KEYS) {
        if (key in record) {
            throw new Error(`${label} must not include forbidden field "${key}"`);
        }
    }
}
export function createQXComponentPrimitiveCatalog(options) {
    assertNoForbidden({ ...options }, 'Component primitive catalog');
    const list = options.primitives.map((primitive) => createQXComponentPrimitive(primitive));
    const seen = new Set();
    for (const primitive of list) {
        if (seen.has(primitive.identity.id)) {
            throw new Error(`Duplicate component primitive id: ${primitive.identity.id}`);
        }
        seen.add(primitive.identity.id);
    }
    return Object.freeze({
        modelVersion: QX_COMPONENT_PRIMITIVES_MODEL_VERSION,
        primitives: Object.freeze(list),
    });
}
export function isQXComponentPrimitiveCatalog(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    return (record.modelVersion === QX_COMPONENT_PRIMITIVES_MODEL_VERSION &&
        Array.isArray(record.primitives));
}
/**
 * Assert every primitive token reference exists in the design-token catalog.
 */
export function validateQXComponentPrimitiveCatalog(catalog, tokens) {
    if (!isQXComponentPrimitiveCatalog(catalog)) {
        throw new Error('Invalid component primitive catalog');
    }
    assertNoForbidden({ ...catalog }, 'Component primitive catalog');
    for (const primitive of catalog.primitives) {
        if (!isQXComponentPrimitive(primitive)) {
            throw new Error('Invalid component primitive in catalog');
        }
        for (const token of primitive.tokens) {
            if (!hasQXDesignToken(tokens, token)) {
                throw new Error(`Primitive "${primitive.identity.id}" references unknown token "${formatQXDesignTokenPath(token)}"`);
            }
        }
    }
    createQXComponentPrimitiveCatalog(catalog);
}
/**
 * Validate catalog tokens against an authoritative design system (via derived tokens).
 */
export function validateQXComponentPrimitivesAgainstSystem(catalog, system) {
    const tokens = createQXDesignTokensFromSystem(system);
    validateQXDesignTokens(tokens, system);
    validateQXComponentPrimitiveCatalog(catalog, tokens);
}
export function listQXComponentPrimitivesByFamily(catalog, family) {
    return catalog.primitives.filter((primitive) => primitive.identity.family === family);
}
export function getQXComponentPrimitive(catalog, id) {
    const primitive = catalog.primitives.find((item) => item.identity.id === id);
    if (!primitive) {
        throw new Error(`Unknown component primitive id "${id}"`);
    }
    return primitive;
}
/** Immutable append — returns a new frozen catalog. */
export function withQXComponentPrimitive(catalog, primitive) {
    return createQXComponentPrimitiveCatalog({
        primitives: [...catalog.primitives, primitive],
    });
}
export function createQXDesignTokensForCatalog(catalog) {
    const seen = new Set();
    const references = [];
    for (const primitive of catalog.primitives) {
        for (const token of primitive.tokens) {
            const key = formatQXDesignTokenPath(token);
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            references.push(token);
        }
    }
    return createQXDesignTokens({ references });
}
//# sourceMappingURL=component-primitives.js.map